import React, { useEffect, useState, useMemo } from "react";
import { Tag, Radio, DatePicker, Input, Modal, Tooltip } from "antd";
import { WarningOutlined, PauseCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { GetAssignedTasksApi } from "../api/tasks/GetAssignedTasksApi";
import HoldTaskApi from "../api/tasks/HoldTaskApi";
import ResumeTaskApi from "../api/tasks/ResumeTaskApi";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import PageLoader from "./sub/PageLoader";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../helpers/NotificationHelpers";

const { RangePicker } = DatePicker;

export default function PositionTasksTab({ positionId, perfCycleId, allowHoldResume = false }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [holdModal, setHoldModal] = useState(false);
  const [holdTask, setHoldTask] = useState(null);
  const [holdReason, setHoldReason] = useState("");
  const [blockedTaskIds, setBlockedTaskIds] = useState([]);

  useEffect(() => {
    if (positionId && perfCycleId) {
      fetchTasks();
    }
  }, [positionId, perfCycleId]);

  async function fetchTasks() {
    setLoading(true);
    try {
      const response = await GetAssignedTasksApi(positionId, perfCycleId);
      if (response.success) {
        setTasks(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
    setLoading(false);
  }

  async function putOnHold() {
    if (!holdReason.trim()) {
      openNotificationError("bottomRight", "Please provide a reason");
      return;
    }
    const apiResponse = await HoldTaskApi(holdTask?.id, holdReason.trim(), blockedTaskIds);
    if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Task put on hold");
      fetchTasks();
    } else {
      openNotificationError("bottomRight", "Couldn't hold task. Try again later.");
    }
    setHoldTask(null);
    setHoldReason("");
    setBlockedTaskIds([]);
    setHoldModal(false);
  }

  async function resumeTask(taskId) {
    const apiResponse = await ResumeTaskApi(taskId);
    if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Task resumed");
      fetchTasks();
    } else {
      openNotificationError("bottomRight", "Couldn't resume task. Try again later.");
    }
  }

  const filteredTasks = useMemo(() => {
    let result = tasks;

    // Status filter
    if (statusFilter === "pending") {
      result = result.filter((task) => !task.completionStatus && !task.onHold);
    } else if (statusFilter === "completed") {
      result = result.filter((task) => task.completionStatus);
    } else if (statusFilter === "onhold") {
      result = result.filter((task) => task.onHold);
    }

    // Date range filter (custom range takes priority over preset)
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startRange = dateRange[0].clone().startOf("day");
      const endRange = dateRange[1].clone().endOf("day");

      result = result.filter((task) => {
        const taskEnd = task.taskEndDate ? moment(task.taskEndDate) : null;
        const taskStart = task.taskStartDate
          ? moment(task.taskStartDate)
          : null;

        if (taskEnd && taskEnd.isBetween(startRange, endRange, null, "[]")) {
          return true;
        }
        if (
          taskStart &&
          taskStart.isBetween(startRange, endRange, null, "[]")
        ) {
          return true;
        }
        if (
          taskStart &&
          taskEnd &&
          taskStart.isBefore(startRange) &&
          taskEnd.isAfter(endRange)
        ) {
          return true;
        }
        return false;
      });
    } else if (filter !== "all") {
      // Preset filter
      const now = moment();
      let startRange;
      let endRange;

      if (filter === "day") {
        startRange = now.clone().startOf("day");
        endRange = now.clone().endOf("day");
      } else if (filter === "week") {
        startRange = now.clone().startOf("isoWeek");
        endRange = now.clone().endOf("isoWeek");
      } else if (filter === "month") {
        startRange = now.clone().startOf("month");
        endRange = now.clone().endOf("month");
      }

      result = result.filter((task) => {
        const taskEnd = task.taskEndDate ? moment(task.taskEndDate) : null;
        const taskStart = task.taskStartDate
          ? moment(task.taskStartDate)
          : null;

        if (taskEnd && taskEnd.isBetween(startRange, endRange, null, "[]")) {
          return true;
        }
        if (
          taskStart &&
          taskStart.isBetween(startRange, endRange, null, "[]")
        ) {
          return true;
        }
        if (
          taskStart &&
          taskEnd &&
          taskStart.isBefore(startRange) &&
          taskEnd.isAfter(endRange)
        ) {
          return true;
        }
        return false;
      });
    }

    return result;
  }, [tasks, filter, dateRange, statusFilter]);

  if (loading) {
    return <PageLoader />;
  }

  if (tasks.length === 0) {
    return (
      <div
        className="flex justify-center items-center"
        style={{ minHeight: 400 }}
      >
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No Tasks available
          </div>
          <div className="text-gray-400">
            No tasks found for plans in this performance cycle
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-500">Period:</span>
        <Radio.Group
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            if (e.target.value !== "all") setDateRange(null);
          }}
          size="small"
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="day">Today</Radio.Button>
          <Radio.Button value="week">This Week</Radio.Button>
          <Radio.Button value="month">This Month</Radio.Button>
        </Radio.Group>

        <RangePicker
          size="small"
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates);
            if (dates) setFilter("all");
          }}
          allowClear
        />

        <span className="text-sm text-gray-500 ml-2">Status:</span>
        <Radio.Group
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="pending">Pending</Radio.Button>
          <Radio.Button value="onhold">On Hold</Radio.Button>
          <Radio.Button value="completed">Completed</Radio.Button>
        </Radio.Group>
      </div>

      {filteredTasks.length === 0 ? (
        <div
          className="flex justify-center items-center"
          style={{ minHeight: 200 }}
        >
          <div className="text-center text-gray-400">
            No tasks found for the selected filters
          </div>
        </div>
      ) : (
        <>
          {filteredTasks.map((task) => (
            <div
              key={task.id}
              className={"flex items-start gap-4 border-b p-4 max-w-4xl" + (task?.onHold ? " bg-yellow-50" : "")}
            >
              <div
                style={{
                  border: task?.onHold ? "1px solid #faad14" : "1px solid #1da57a",
                  backgroundColor: task?.completionStatus ? "#1da57a" : task?.onHold ? "#faad14" : "transparent",
                }}
                className="h-4 w-4 border rounded-sm mt-1"
              />

              <div className="flex items-start justify-between w-full">
                <div>
                  <div
                    className={`font-semibold ${task?.completionStatus ? "line-through" : ""} ${task?.onHold ? "text-yellow-700" : ""} cursor-pointer hover:text-blue-500`}
                    onClick={() => navigate(`/top-plans/${task.planId}`)}
                  >
                    {task.name}
                    {task?.onHold && (
                      <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">
                        On Hold{task.holdReason ? ` · ${task.holdReason}` : ""}
                      </span>
                    )}
                    {task?.blockedBy && (
                      <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                        Blocking: {task.blockedBy.name}
                      </span>
                    )}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">
                    Plan: {task.plan?.title || "-"}
                  </div>
                  {/* Owner info */}
                  {task.positions?.users && (
                    <div className="flex items-center gap-2 mt-2">
                      {task.positions.users.imageUrl ? (
                        <img src={task.positions.users.imageUrl} alt="" style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" }}>
                          {`${task.positions.users.firstName || ""} ${task.positions.users.lastName || ""}`.trim().split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                        </div>
                      )}
                      <span className="text-xs text-gray-500">
                        {`${task.positions.users.firstName || ""} ${task.positions.users.lastName || ""}`.trim()}
                        {task.positions.designations?.name ? ` · ${task.positions.designations.name}` : ""}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-5 text-xs mt-2 text-gray-500">
                    {task?.taskStartDate && (
                      <div>
                        Start:{" "}
                        {moment(task.taskStartDate).format("MMM DD, YYYY")}
                      </div>
                    )}
                    {task?.taskEndDate && (
                      <div>
                        Target:{" "}
                        {moment(task.taskEndDate).format("MMM DD, YYYY")}
                      </div>
                    )}
                    {task?.completionStatus && task?.completionDate && (
                      <div>
                        Completed:{" "}
                        {moment(task.completionDate).format("MMM DD, YYYY")}
                      </div>
                    )}
                  </div>
                </div>
              <div className="flex items-center gap-2">
                <Tag color={task?.completionStatus ? "green" : task?.onHold ? "warning" : task?.blockedBy ? "orange" : "blue"}>
                  {task?.completionStatus ? "Completed" : task?.onHold ? "On Hold" : task?.blockedBy ? "Blocking" : "Pending"}
                </Tag>
                {allowHoldResume && !task?.completionStatus && !task?.onHold && (
                  <Tooltip title="Put on Hold">
                    <PauseCircleOutlined
                      className="text-yellow-500 hover:text-yellow-600 cursor-pointer text-base"
                      onClick={() => {
                        setHoldTask(task);
                        setHoldModal(true);
                      }}
                    />
                  </Tooltip>
                )}
                {allowHoldResume && task?.onHold && (
                  <Tooltip title="Resume">
                    <PlayCircleOutlined
                      className="text-green-500 hover:text-green-600 cursor-pointer text-base"
                      onClick={() => resumeTask(task.id)}
                    />
                  </Tooltip>
                )}
              </div>
              </div>
            </div>
          ))}
          <div className="mt-4 text-gray-500 text-sm">
            Showing: {filteredTasks.length} of {tasks.length}{" "}
            {tasks.length === 1 ? "task" : "tasks"}
          </div>
        </>
      )}
      {allowHoldResume && (
        <Modal
          title="Put Task on Hold"
          visible={holdModal}
          onCancel={() => { setHoldModal(false); setHoldTask(null); setHoldReason(""); setBlockedTaskIds([]); }}
          onOk={putOnHold}
          okText="Put on Hold"
          okButtonProps={{ disabled: !holdReason.trim() }}
        >
          <div className="mb-2 text-sm text-gray-600">
            Provide a reason for putting "{holdTask?.name}" on hold:
          </div>
          <Input.TextArea
            value={holdReason}
            onChange={(e) => { if (e.target.value.length <= 100) setHoldReason(e.target.value); }}
            placeholder="Reason for hold..."
            rows={3}
            maxLength={100}
            showCount
          />
          {tasks.filter(t => t.id !== holdTask?.id && !t.completionStatus).length > 0 && (
            <div className="mt-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Which tasks need to finish before this can resume? <span className="text-gray-400 font-normal">(optional)</span>
              </div>
              <div className="border rounded-md max-h-40 overflow-y-auto divide-y">
                {tasks
                  .filter(t => {
                    if (t.id === holdTask?.id || t.completionStatus) return false;
                    // Exclude tasks that are already waiting on THIS task (would create circular dep)
                    if (t.onHold && t.blockedTaskIds) {
                      let ids = [];
                      try { ids = Array.isArray(t.blockedTaskIds) ? t.blockedTaskIds : JSON.parse(t.blockedTaskIds); } catch { ids = []; }
                      if (ids.includes(holdTask?.id)) return false;
                    }
                    return true;
                  })
                  .map(t => {
                    const alreadyDepFor = tasks.filter(other =>
                      other.id !== holdTask?.id && other.onHold && other.blockedTaskIds &&
                      (Array.isArray(other.blockedTaskIds) ? other.blockedTaskIds : (() => { try { return JSON.parse(other.blockedTaskIds); } catch { return []; } })()).includes(t.id)
                    );
                    return (
                      <label key={t.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={blockedTaskIds.includes(t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBlockedTaskIds(prev => [...prev, t.id]);
                            } else {
                              setBlockedTaskIds(prev => prev.filter(id => id !== t.id));
                            }
                          }}
                        />
                        <span className="text-sm text-gray-700">{t.name}</span>
                        {t.onHold && (
                          <span className="text-xs text-yellow-600 ml-1">(on hold)</span>
                        )}
                        {alreadyDepFor.length > 0 && (
                          <span className="text-xs text-orange-500 ml-1">(also blocking {alreadyDepFor[0].name})</span>
                        )}
                        {t.taskEndDate && (
                          <span className="text-xs text-gray-400 ml-auto">
                            due {moment(t.taskEndDate).format("MMM DD")}
                          </span>
                        )}
                      </label>
                    );
                  })}
              </div>
              {blockedTaskIds.length > 0 && (
                <div className="mt-1 text-xs text-orange-600">
                  {blockedTaskIds.length} task{blockedTaskIds.length > 1 ? "s" : ""} must finish before this can resume
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
